import { Button } from '@finn-auto/design-system/atoms/button';
import { CandidateChips } from './CandidateChips';
import { FeedbackByline } from './FeedbackByline';
import { cn } from '../../../../shared';
import type {
  FeedbackItem,
  SchedulingCandidate,
  TrackingDoc,
} from '../../types/taActionBoard';
import { COLORS, firstNameFromEmail, fmtDate, fullNameFromEmail, ratingChipStyle } from './format';
import { CancellationTag } from './CancellationTag';
import { PhoneChip } from './PhoneChip';
import { HandledChip } from './HandledChip';
import { ReplyChip } from './ReplyChip';
import type { CandidateReply } from './candidateReplies';
import { StatusBadge, TrackingTable } from './TrackingTable';

// ───────────────────────────── FEEDBACK CARD ─────────────────────────────

export type FeedbackAction =
  | { type: 'archive'; item: FeedbackItem }
  | { type: 'move'; item: FeedbackItem }
  // Take the card off the overview without an archive or a move (e.g. a no-show already rebooked).
  | { type: 'discard'; item: FeedbackItem };

interface FeedbackCardProps {
  item: FeedbackItem;
  highlighted?: boolean;
  /** "Archived" / "Moved to Case" … — mutes the card, hides the actions. */
  handledLabel?: string | null;
  onAction: (action: FeedbackAction) => void;
}

/** Muted "already managed" chip, shared by all card types — lives in ./HandledChip. */
export { HandledChip };

/**
 * Feedback decision card — white on Cotton. Header row (name + Lever link +
 * stage · date + decision badge) with a hairline divider; bullets one per
 * line; action column behind a vertical divider.
 */
/** "Case / Technical Interview" → "Case" — full name stays in the tooltip + modal. */
function shortStage(stageName: string): string {
  return stageName.replace(/\s*\/.*$/, '').replace(/\s*Interview\s*$/i, '').trim() || stageName;
}

/** A feedback bullet is server-prefixed ✅ (strength) or ⚠️ (concern); split that off for a dot. */
function parseBullet(raw: string): { kind: 'pro' | 'con'; text: string } {
  const t = raw.trim();
  if (t.startsWith('⚠️')) return { kind: 'con', text: t.replace(/^⚠️️?\s*/, '') };
  if (t.startsWith('✅')) return { kind: 'pro', text: t.replace(/^✅️?\s*/, '') };
  return { kind: 'pro', text: t };
}

/**
 * Clean rating badge label from the server's "{dot} {n} - {Label}" string:
 * split ratings read as "Mixed feedback", everything else keeps its Lever label
 * in sentence case ("Strong Hire" → "Strong hire"). Colour comes from ratingChipStyle.
 */
function feedbackBadgeLabel(item: FeedbackItem): string {
  if (item.decision === 'review' || /mixed/i.test(item.ratingLabel || '')) return 'Mixed feedback';
  const raw = item.ratingLabel || item.consensus || '';
  const label = raw.replace(/^[^A-Za-z]*/, '').replace(/^\d+\s*-\s*/, '').trim() || item.consensus;
  return label ? label.charAt(0).toUpperCase() + label.slice(1).toLowerCase() : item.consensus;
}

export function FeedbackCard({ item, highlighted, handledLabel, onAction }: FeedbackCardProps) {
  const isHire = item.decision === 'hire';
  /**
   * BOTH ways forward on EVERY card: the feedback's reading is a suggestion, the decision is the
   * recruiter's (Theresa, Sep 2026) — they may know of a withdrawal, a frozen role or an offer
   * taken elsewhere. Gating either direction sent exactly that disagreement case off to Lever.
   * The only thing that can still remove the move is Lever having no next stage to move TO.
   */
  const canMove = Boolean(item.nextStageId);
  /**
   * PROMINENCE, not availability, carries the recommendation: quiet grey where the action
   * contradicts a clear reading, full weight otherwise. A MIXED rating leaves the way forward the
   * louder of the two — the interviewers disagreed, and the next round is what resolves that.
   */
  const archiveIsLouder = item.decision === 'no-hire';
  // Only a CLEAR reading is worth contradicting out loud; a split recommends neither.
  const contradictsArchive = isHire;
  const contradictsMove = archiveIsLouder;
  const chip = ratingChipStyle(item.ratingLabel);
  return (
    <div
      id={`ta-card-${item.opportunityId}`}
      // Focusable only programmatically — useFocusCard moves keyboard focus here on arrival.
      tabIndex={-1}
      className={cn(
        'rounded-lg border border-gray-200 bg-white p-3.5 transition-all',
        highlighted && 'ring-2 ring-blue-400',
        handledLabel && 'opacity-45'
      )}
    >
      {/* identity + meta on the left, the rating badge top-right */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="truncate text-[15px] font-semibold text-gray-900">{item.name}</div>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-400">
            <span>{item.stageName}</span>
            {/* Who assessed them, and the two dates kept apart — see FeedbackByline. */}
            <FeedbackByline
              interviewers={item.interviewers}
              interviewAt={item.interviewAt}
              completedAt={item.completedAt}
            />
            {item.ageDays > 7 && (
              <span
                className="rounded px-1.5 py-0.5 text-[11px] font-semibold"
                style={{ backgroundColor: COLORS.redBg, color: COLORS.redText }}
              >
                {Math.round(item.ageDays)} days waiting
              </span>
            )}
            <a
              href={item.leverUrl}
              target="_blank"
              rel="noreferrer"
              className="text-blue-600 hover:underline"
              title="Open in Lever"
            >
              Lever ↗
            </a>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <CandidateChips candidateName={item.name} opportunityId={item.opportunityId} />
          <span
            className="rounded px-2 py-0.5 text-xs font-semibold"
            style={{ backgroundColor: chip.bg, color: chip.text }}
          >
            {feedbackBadgeLabel(item)}
          </span>
        </div>
      </div>

      {/* one-line verdict (server summary), when present */}
      {item.summary && <p className="mt-2.5 text-[13px] leading-snug text-gray-700">{item.summary}</p>}
      {item.modelPending && !item.summary && (
        <p className="mt-2.5 text-[13px] italic leading-snug text-gray-400">Summarising the feedback…</p>
      )}

      {/* strengths / concerns as coloured dots (parsed from the ✅/⚠️ prefix) */}
      {item.bullets.length > 0 ? (
        <ul className="mt-2.5 flex flex-col gap-1.5">
          {item.bullets.map((b, i) => {
            const { kind, text } = parseBullet(b);
            return (
              <li key={i} className="flex items-start gap-2 text-[13px] leading-snug text-gray-700">
                <span
                  aria-label={kind === 'con' ? 'Concern' : 'Strength'}
                  className="mt-[5px] h-[7px] w-[7px] shrink-0 rounded-full"
                  style={{ backgroundColor: kind === 'con' ? COLORS.statusOrange : COLORS.statusGreen }}
                />
                <span>{text}</span>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="mt-2.5 text-[13px] italic text-gray-400">
          No summary available — see feedback in Lever.
        </p>
      )}

      {/* actions as a bottom row: Advance primary when there's a way forward, else Archive primary */}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        {handledLabel ? (
          <HandledChip label={handledLabel} />
        ) : (
          <>
            {canMove && (
              <Button
                size="sm"
                variant={archiveIsLouder ? 'ghost' : undefined}
                onClick={() => onAction({ type: 'move', item })}
                title={
                  contradictsMove
                    ? `The feedback recommends archiving — advance to ${item.nextStageName} anyway`
                    : `Advance to ${item.nextStageName}`
                }
              >
                Advance to {shortStage(item.nextStageName)}
              </Button>
            )}
            {/* ONE flow either way: the archive modal carries the reason select AND the editable
                rejection draft; the move modal carries the full invite for the next round. */}
            <Button
              size="sm"
              variant={archiveIsLouder ? undefined : 'ghost'}
              onClick={() => onAction({ type: 'archive', item })}
              title={contradictsArchive ? 'The feedback recommends advancing — archive anyway' : undefined}
            >
              Archive
            </Button>
            {/* Neither advancing nor archiving fits: just take the card off this overview. */}
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onAction({ type: 'discard', item })}
              title="Remove this card from the Feedback overview"
            >
              Discard
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

// ───────────────────────────── SCHEDULING CARD ─────────────────────────────

// NOTE: "📌 Block selected slots" and "💬 Ask panelists…" are deliberately NOT
// here — HOLD events and the panelists' Slack message are per ROLE (one set of
// slots for the panel), so those actions live on the role's panel card (SlotList).
export type SchedulingAction =
  | {
      type: 'offered';
      candidate: SchedulingCandidate;
      /**
       * ISO starts ticked in the panel's slot list. Filled in by RoleScheduling,
       * not the card — the card cannot see the slot list, and without this the
       * modal fell back to "the first suggestion", which is how a candidate came
       * to be emailed times the recruiter had not chosen.
       */
      preselected?: string[];
    }
  | { type: 'confirmed'; candidate: SchedulingCandidate }
  | { type: 'withdraw'; candidate: SchedulingCandidate };

interface SchedulingCardProps {
  candidate: SchedulingCandidate;
  tracking: TrackingDoc | undefined;
  /** "Interview confirmed" … — mutes the card; actions stay usable. */
  handledLabel?: string | null;
  /** Arrived at from the Overdue tab — ringed, like a focused feedback card. */
  highlighted?: boolean;
  /** Their latest reply about times, if the Requests queue is loaded (useCandidateReplies). */
  reply?: CandidateReply;
  onAction: (action: SchedulingAction) => void;
}

/**
 * Scheduling card — identity header with a prominent phone chip and tracking
 * status badge, interview trail mini-table, slot tracking table and actions.
 */
/**
 * The times proposed to this candidate, with the grade each had when it was
 * proposed (stored on the kind=offered tracking entry). Reads the most recent
 * proposal; a later withdrawal clears it.
 */
/**
 * The times last proposed to a candidate FOR ONE STAGE. Scoped by `kind` on
 * purpose: a tracking doc can hold both case ('offered') and final-round
 * ('fr-proposed') entries, and a card must never show the other stage's times.
 * Uses graded slotMeta when the flow stored it (case does); otherwise the plain
 * proposed times (final), shown without a grade badge.
 */
function latestProposedTimes(
  tracking: TrackingDoc | undefined,
  kind: 'offered' | 'fr-proposed'
): { label: string; grade: string }[] {
  const entries = tracking?.entries || [];
  for (let i = entries.length - 1; i >= 0; i--) {
    const e = entries[i];
    // 'withdrawn' is a CASE-stage reset — it must not blank the final card's
    // proposed times (and vice versa), so only honor it for the case lookup.
    if (kind === 'offered' && e.kind === 'withdrawn') return [];
    if (e.kind === kind) {
      if (Array.isArray(e.slotMeta) && e.slotMeta.length > 0) return e.slotMeta;
      if (Array.isArray(e.slots) && e.slots.length > 0) return e.slots.map((label) => ({ label, grade: '' }));
    }
  }
  return [];
}

/**
 * The "N times proposed" table — the graded slots last offered to a candidate
 * for THIS stage. Shared by the case ('offered') and final-round ('fr-proposed')
 * cards. A free/needs-move badge shows only when the flow graded the slot.
 */
export function ProposedTimesTable({
  tracking,
  kind,
}: {
  tracking?: TrackingDoc;
  kind: 'offered' | 'fr-proposed';
}) {
  const proposedTimes = latestProposedTimes(tracking, kind);
  if (proposedTimes.length === 0) return null;
  return (
    <div className="px-4 pt-3">
      <div className="mb-1 text-xs text-gray-400">
        {proposedTimes.length} time{proposedTimes.length === 1 ? '' : 's'} proposed
      </div>
      <div className="overflow-hidden rounded-md border border-gray-200">
        {proposedTimes.map((t, i) => (
          <div
            key={i}
            className={cn(
              'flex items-center justify-between gap-3 px-3 py-2 text-[13px]',
              i > 0 && 'border-t border-gray-100'
            )}
          >
            <span className="text-gray-800">{t.label}</span>
            {(t.grade === 'free' || t.grade === 'needs-move') && (
              <span
                className="rounded px-2 py-0.5 text-[11px] font-semibold"
                style={
                  t.grade === 'needs-move'
                    ? { backgroundColor: COLORS.orangeBg, color: COLORS.orangeText }
                    : { backgroundColor: COLORS.greenBg, color: COLORS.greenText }
                }
              >
                {t.grade === 'needs-move' ? 'Needs a meeting moved' : 'Free'}
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export function SchedulingCard({
  candidate,
  tracking,
  highlighted,
  reply,
  handledLabel,
  onAction,
}: SchedulingCardProps) {
  const status = tracking?.status || 'not started';
  /** Times are out with this candidate — booking is the next step, not proposing. */
  const offered = status === 'offered';
  return (
    <div
      // Landing target for the Overdue tab's stuck chips (useFocusCard scrolls to this id).
      id={`ta-card-${candidate.opportunityId}`}
      tabIndex={-1}
      className={cn(
        'rounded-lg border border-gray-200 bg-white transition-all',
        highlighted && 'ring-2 ring-blue-400',
        handledLabel && 'opacity-45'
      )}
    >
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-4 py-3">
        <div className="flex min-w-0 items-center gap-2">
          <span className="truncate text-sm font-semibold text-gray-900">{candidate.name}</span>
          <a
            href={candidate.leverUrl}
            target="_blank"
            rel="noreferrer"
            className="shrink-0 text-xs text-blue-600 hover:underline"
          >
            Lever ↗
          </a>
          <span className="shrink-0 text-xs text-gray-400">
            waiting {candidate.waitingDays.toFixed(1)}d
          </span>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <ReplyChip reply={reply} />
          <CandidateChips candidateName={candidate.name} opportunityId={candidate.opportunityId} />
          {handledLabel && <HandledChip label={handledLabel} />}
          <PhoneChip phone={candidate.phone} />
          <StatusBadge status={status} />
        </div>
      </div>

      {/* WHY they are back on this list. Above the proposed times on purpose:
          those times are the ones that were declined, and reading them first
          without the cancellation is how a recruiter re-proposes them. */}
      {candidate.cancellation && (
        <div className="px-4 pt-3">
          <CancellationTag cancellation={candidate.cancellation} />
        </div>
      )}

      <ProposedTimesTable tracking={tracking} kind="offered" />

      <div className="grid gap-4 px-4 py-3 lg:grid-cols-2">
        <InterviewTrail candidate={candidate} />
        {/* Case scheduling reads its OWN log — final-round entries belong to that
            section's cards. */}
        <TrackingTable tracking={tracking} scope="case" />
      </div>

      <div className="flex flex-wrap gap-2 border-t border-gray-100 px-4 py-3">
        {/*
          The next step leads. Three identical outline buttons said nothing
          about what to do next, so a card with times already out still invited
          the recruiter to propose more — and booking, the actual next step,
          looked like an alternative to it (Theresa, Sep 2026). Once a time has
          been offered the primary action is booking it; before that it is
          proposing. Both stay available either way: a candidate who cannot make
          any of the offered times needs re-proposing, not a dead end.
        */}
        <Button
          size="sm"
          variant={offered ? 'outline' : 'primary'}
          onClick={() => onAction({ type: 'offered', candidate })}
        >
          {offered ? 'Propose other times' : 'Propose times'}
        </Button>
        <Button
          size="sm"
          variant={offered ? 'primary' : 'outline'}
          onClick={() => onAction({ type: 'confirmed', candidate })}
        >
          Book case interview
        </Button>
        {(status === 'offered' || status === 'confirmed') && (
          <Button size="sm" variant="outline" onClick={() => onAction({ type: 'withdraw', candidate })}>
            Withdraw proposed times
          </Button>
        )}
      </div>
    </div>
  );
}

/** 🧭 INTERVIEW TRAIL mini-table: stage | interviewer full names | date. */
export function InterviewTrail({ candidate }: { candidate: SchedulingCandidate }) {
  const metEmails = new Set(
    candidate.trail.flatMap((t) => t.interviewerEmails.map((e) => e.toLowerCase()))
  );
  const suggested = candidate.suggestedPanel || [];
  // Resolved BY THE SERVER for this candidate from the role's pool — pinned
  // people first, then faces they have not met — so this just reads off that
  // decision (see resolveCandidatePanel).
  const pinned = candidate.panelFixed || [];
  return (
    <div>
      <div className="mb-1 text-[11px] font-semibold tracking-wide text-gray-400">
        Interview history
      </div>
      <table className="w-full text-xs">
        <tbody>
          {candidate.trail.map((t, i) => (
            <tr key={i} className="border-b border-gray-100 last:border-0">
              <td className="py-1.5 pr-3 text-gray-500">{t.stageName}</td>
              <td className="py-1.5 pr-3 text-gray-700">{t.interviewers.join(', ') || '—'}</td>
              <td className="whitespace-nowrap py-1.5 text-right text-gray-400">{fmtDate(t.date)}</td>
            </tr>
          ))}
          {suggested.length > 0 && (
            <tr>
              <td className="py-1.5 pr-3 text-gray-500">Case (suggested)</td>
              <td className="py-1.5 pr-3 text-gray-700">
                {suggested.map((email) => (
                  <span key={email} className="mr-2 inline-flex items-center gap-1">
                    {fullNameFromEmail(email)}
                    {!metEmails.has(email.toLowerCase()) && (
                      <span
                        className="rounded-full px-1.5 py-0.5 text-[10px] font-semibold"
                        style={{ backgroundColor: COLORS.orangeBg, color: COLORS.orangeText }}
                      >
                        not met yet
                      </span>
                    )}
                  </span>
                ))}
              </td>
              <td className="py-1.5 text-right text-gray-400">—</td>
            </tr>
          )}
          {candidate.trail.length === 0 && suggested.length === 0 && (
            <tr>
              <td colSpan={3} className="py-1.5 text-gray-400">
                No interviews yet.
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {suggested.length > 0 && (pinned.length > 0 || candidate.panelShort) && (
        <p
          className="mt-1.5 rounded-md px-2 py-1 text-[11px]"
          style={
            candidate.panelShort
              ? { backgroundColor: COLORS.orangeBg, color: COLORS.orangeText }
              : { backgroundColor: COLORS.blueBg, color: COLORS.blueText }
          }
        >
          {candidate.panelShort
            ? 'Not enough people in the panel pool to fill every seat — edit the panel.'
            : `${pinned.map(firstNameFromEmail).join(' and ')} ${
                pinned.length === 1 ? 'is a fixed panelist' : 'are fixed panelists'
              } for this role, so ${pinned.length === 1 ? 'they take a seat' : 'they take seats'} whatever ${candidate.name.split(' ')[0]} has already seen.`}
        </p>
      )}
    </div>
  );
}
